/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { AlertType } from 'foundations-library/types'
import { Alert, Button, Typography, Icon } from 'foundations-library/components'
import { useAccountSecurityContext } from 'providers'
import { Dialog, LoaderDialogContent, FormButtons } from 'functional-foundations'
import { redirectLogin } from 'phonecom'
import { ErrorDialog } from 'functional-foundations/dialogs'
import { getMuiTheme } from '../../utils'
import PasswordManagementForm from '../elements/PasswordManagementForm'

const jss = (theme) => {
    return {
        passwordManagement: ({ theme: optionsTheme }) => {
            if (optionsTheme === 'mui') return getMuiTheme()

            if (optionsTheme === 'foundations') return {}
        },
        alert: {
            marginBottom: '20px',
            maxWidth: '660px',
            paddingLeft: '30px',
            margin: '0 auto'
        },
        formContainer: {
            marginBottom: '50px',
            margin: 'auto'
        },
        createPasswordDialog: {
            '& .dialog-content': {
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '30px'
            }
        },
        dialogHeadingContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px',
            alignSelf: 'stretch'
        },
        buttonsContainer: {
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            alignItems: 'center',
            marginBottom: ({ openInDialog }) => openInDialog ? '0px' : '50px'
        },
        submitBtn: {
            width: '100%'
        },
        confirmDialogText: {
            marginBottom: '60px'
        },
        successPasswordDialogUpdate: {
            '& .dialog-content': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '50px',
                height: '370px'
            }
        },
        successPasswordDialogCreate: {
            '& .dialog-content': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '80px',
                alignSelf: 'stretch'
            }
        },
        appLoaderIcon: {
            ...theme.appLoader.logo,
            width: '100px',
            height: '100px'
        },
        successPasswordDialogCreateContent: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '50px',
            alignSelf: 'stretch'
        },
        successPasswordDialogCreateContentText: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px',
            alignSelf: 'stretch'
        }
    }
}

const useStyles = makeStyles(jss)

type PropsType = {
    mode?: 'update' | 'create',
    openInDialog?: boolean,
    onSave?: (result: 'success' | 'error', err?: any) => any
    logout?: () => any // when used as script tag, we can provide platform where it is used logout callback
    onRedirectToLogin?: () => void,
    theme?: 'foundations' | 'mui' // delete all related mentions as soon as signup updates design
    mfaFlow?: boolean
}

const makePromise = () => new Promise((resolve) => { resolve(null) })

const openDialog = (testId: string) => {
    const dialog = document.querySelector(`[data-testid="${testId}"]`)
    if (dialog) {
        (dialog as HTMLDialogElement).showModal()
    }
}
const closeDialog = (testId: string) => {
    const dialog = document.querySelector(`[data-testid="${testId}"]`)
    if (dialog) {
        (dialog as HTMLDialogElement).close()
    }
}

const passwordExpiryDuration = 180 // days

const passwordDialogTestId = 'password-dialog'
const successPasswordDialogUpdateTestId = 'success-password-dialog-update'
const successPasswordDialogCreateTestId = 'success-password-dialog-create'
const loadingDialogTestId = 'loading-dialog'
const confirmDialogTestId = 'confirm-dialog'
const mfaErrorDialogTestId = 'mfa-error-dialog'
/**
 *
 */
const PasswordManagement = ({
    mode = 'update',
    onSave,
    logout,
    onRedirectToLogin,
    theme = 'foundations',
    resetPasswordManagement,
    openInDialog = false,
    mfaFlow = false
}: PropsType & { resetPasswordManagement: () => void }): JSX.Element => {
    const styles = useStyles({ mode, theme, openInDialog })
    const [loading, setLoading] = useState(false)
    const [formIsValid, setFormIsValid] = useState(false)
    const [newPasswordValue, setNewPasswordValue] = useState(null)
    const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
    const [formValues, setFormValues] = useState({ current: '', new: '', confirm: '' })
    const [passwordChangeResult, setPasswordChangeResult] = useState<null | AlertType>(null)
    const [openPasswordDialog, setOpenPasswordDialog] = useState(openInDialog)
    const [mfaError, setMfaError] = useState(null)
    const { setDevices, client, updateSecurityData } = useAccountSecurityContext()

    // manual handling of dialogs as native dialog does not work well with React states
    useEffect(() => {
        if (passwordChangeResult?.level === 'success' && mode === 'update') {
            openDialog(successPasswordDialogUpdateTestId)
        } else if (passwordChangeResult?.level === 'success' && mode === 'create') {
            openDialog(successPasswordDialogCreateTestId)
        }
        if (passwordChangeResult?.level !== 'success') {
            closeDialog(successPasswordDialogUpdateTestId)
            closeDialog(successPasswordDialogCreateTestId)
        }
    }, [passwordChangeResult?.level])

    useEffect(() => {
        if (loading) {
            openDialog(loadingDialogTestId)
        } else {
            closeDialog(loadingDialogTestId)
        }
    }, [loading])

    const shouldPasswordDialogBeOpen = openPasswordDialog && !['success', 'complete'].includes(passwordChangeResult?.level) && !loading && !mfaError
    useEffect(() => {
        if (shouldPasswordDialogBeOpen) {
            openDialog(passwordDialogTestId)
        } else {
            closeDialog(passwordDialogTestId)
        }
    }, [shouldPasswordDialogBeOpen])

    useEffect(() => {
        if (confirmDialogIsOpen) {
            openDialog(confirmDialogTestId)
        } else {
            closeDialog(confirmDialogTestId)
        }
    }, [confirmDialogIsOpen])

    useEffect(() => {
        if (mfaError) {
            openDialog(mfaErrorDialogTestId)
        } else {
            closeDialog(mfaErrorDialogTestId)
        }
    }, [mfaError])

    const handlePasswordChangeResult = (level, message = '') => {
        if (!level) setPasswordChangeResult(null)
        setPasswordChangeResult({
            level,
            children: message || (level === 'error' ? 'Something went wrong' : '')
        })
    }

    const triggerOnSaveCallback = (res, err = undefined) => {
        onSave && onSave(res, err)
    }

    const updateAccountSecurityDataForPasswordExpiry = () => {
        updateSecurityData({
            passwordExpiryDate: new Date(new Date().getTime() + passwordExpiryDuration * 24 * 60 * 60 * 1000).toISOString(),
            daysRemainedForPasswordExpiry: passwordExpiryDuration
        })
    }

    const handlePasswordChange = async () => {
        return await client.setPassword({
            password: newPasswordValue,
            currentPassword: formValues.current
        })
        // return await makePromise()
    }

    const onSubmit = async (e) => {
        e && e.preventDefault()
        if (openInDialog) return onConfirm()

        try {
            // if (mode === 'create') {
            //     await handlePasswordChange()
            //     triggerOnSaveCallback('success')
            // } else {
            //     setConfirmDialogIsOpen(true) // skipping the dialog for now
            //     onConfirm()
            // }
            onConfirm()
        } catch (err) {
            console.error(err)
            handlePasswordChangeResult('error', err?.message)
            triggerOnSaveCallback('error', err)
            throw err
        }
    }

    const onConfirm = async () => {
        setConfirmDialogIsOpen(false)
        setLoading(true)
        try {
            // await setDevices() // skipping the device fetch for now
            // fetch devices first to handle any device fetch errors before updating password
            // & avoid showing the loading dialog for fetching devices
            await handlePasswordChange()
            if (mfaFlow) {
                try {
                    await client.enableMFA()
                    redirectToLogin(null, { password_changed: 'true' })
                } catch (err) {
                    console.error('MFA Enable error: ', err?.message)
                    setMfaError(err?.message)
                    setLoading(false)
                }
            } else {
                handlePasswordChangeResult('success')
                setLoading(false)
            }

            updateAccountSecurityDataForPasswordExpiry()
        } catch (err) {
            console.error(err)
            handlePasswordChangeResult('error', err?.message)
            setLoading(false)
        }
    }

    const closeMfaError = () => {
        setMfaError(null)
        handlePasswordChangeResult('success')
    }

    const onForgotPasswordClick = () => {
        alert('Forgot new password demo')
    }

    const redirectToLogin = (hostUrl = null, queryParams = {}) => {
        onRedirectToLogin ? onRedirectToLogin() : redirectLogin(hostUrl, queryParams)
    }

    const handleCloseSuccessDialog = () => {
        handlePasswordChangeResult('complete')
        if (!openInDialog) resetPasswordManagement()
    }

    const renderSuccessAlertForUpdate = () => {
        return (
            <Dialog className={styles.successPasswordDialogUpdate} onClose={handleCloseSuccessDialog} data-testid={successPasswordDialogUpdateTestId}>
                <Icon name='check-alt' size={100} color='primary' />
                <Typography variant='h6' color='neutral-900' align='center'>Your password has been updated!</Typography>
            </Dialog>
        )
    }

    const renderSuccessAlertForCreate = () => {
        return (
            <Dialog className={styles.successPasswordDialogCreate} onClose={handleCloseSuccessDialog} data-testid={successPasswordDialogCreateTestId}>
                <div className={styles.successPasswordDialogCreateContent}>
                    <div className={styles.appLoaderIcon}></div>
                    <div className={styles.successPasswordDialogCreateContentText}>
                        <Typography variant='h6' color='neutral-900'>Your password has been updated!</Typography>
                        <Typography variant='body1' color='neutral-900'>For your security, sign in with your new password.</Typography>
                    </div>
                </div>
                <Button size='large' icon='go' color='primary' variant='contained' onClick={handleCloseSuccessDialog}>Sign in</Button>
            </Dialog>
        )
    }

    const renderForm = () => {
        const form = (<form onSubmit={onSubmit}>
            {passwordChangeResult?.level === 'error' && <Alert onClose={() => handlePasswordChangeResult(null)} autocloseAfter={2500} className={styles.alert} {...passwordChangeResult} />}
            <PasswordManagementForm
                isValid={setFormIsValid}
                onChange={setNewPasswordValue}
                onChangeValues={setFormValues}
                mode={mode}
                className={styles.formContainer}
            />
            <div className={styles.buttonsContainer}>
                {/* {mode === 'update' && <Button
                    onClick={onForgotPasswordClick}
                    size='large'
                    variant='text'
                    icon='go'
                    type='button'
                >Forgot password</Button>} */}
                <Button
                    size='large'
                    icon='go'
                    className={`${styles.submitBtn} submit-button`}
                    type='submit'
                    disabled={!formIsValid || passwordChangeResult?.level === 'error'}
                >{mode === 'create' ? 'Create' : 'Change'} password</Button>
            </div>
        </form>)

        let formComponent = form

        if (openInDialog) {
            formComponent = (
                <Dialog size='medium' className={styles.createPasswordDialog} onClose={() => setOpenPasswordDialog(false)} closeOnClickOutside={false} data-testid={passwordDialogTestId}>
                    <div className={styles.dialogHeadingContainer}>
                        <Typography variant='h6' align='center'>Create a new password</Typography>
                        <Typography variant='body1' align='center'>
                            To complete MFA setup, please create a new password. This helps keep your account secure.
                        </Typography>
                    </div>
                    {form}
                </Dialog>
            )
        }

        return formComponent
    }

    return (
        <div className={`${styles.passwordManagement} password-management`}>
            {passwordChangeResult?.level === 'success' && (mode === 'update' ? renderSuccessAlertForUpdate() : renderSuccessAlertForCreate())}
            {mode === 'update' ? renderSuccessAlertForUpdate() : renderSuccessAlertForCreate()}
            {renderForm()}
            <Dialog data-testid={confirmDialogTestId}>
                <Typography
                    variant='h4'
                    align='center'
                    className={styles.confirmDialogText}
                >Are you sure you want to change your password?</Typography>
                <FormButtons
                    cancelText='Go back'
                    confirmText='Yes, change my password'
                    cancelProps={{ color: 'neutral', variant: 'outlined' }}
                    onCancel={() => setConfirmDialogIsOpen(false)}
                    onConfirm={onConfirm}
                />
            </Dialog>
            <Dialog data-testid={loadingDialogTestId}>
                <LoaderDialogContent
                    title='Updating your password...'
                    subtitle='Please wait!'
                />
            </Dialog>
            <ErrorDialog data-testid={mfaErrorDialogTestId} onClose={closeMfaError} title='Failed to enable MFA.' subtitle={mfaError} onConfirm={closeMfaError} />
            {/* {
                passwordChangeResult?.level === 'success' &&
                <SessionManagement hasPasswordUpdated={true} logout={logout} onRedirectToLogin={onRedirectToLogin} />
            } */}
        </div>
    )
}

/**
 *
 */
const App = (props: PropsType) => {
    const [passwordManagement, setPasswordManagement] = useState(true)

    const resetPasswordManagement = () => {
        setPasswordManagement(false)
        makePromise().then(() => setPasswordManagement(true))
    }

    return passwordManagement ? <PasswordManagement {...props} resetPasswordManagement={resetPasswordManagement} /> : null
}

export default App
